import { useEffect } from "react";
import LazyMap from "@/components/site/LazyMap";
import { Link } from "@/lib/router-compat";
import {
  MapPin,
  Check,
  Shield,
  Phone,
  Lock,
  Car,
  Clock,
  Monitor,
  Calendar,
  Pill,
  Network,
} from "lucide-react";
import Header from "@/components/site/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/site/Footer";
import FloatingCTAs from "@/components/site/FloatingCTAs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import InsurancePills from "@/components/site/InsurancePills";
import RelatedLinks from "@/components/site/RelatedLinks";

const PHONE = "856-200-3127";
const TEL = "tel:8562003127";
const CANONICAL =
  "https://cherryhillrecoverycenter.com/addiction-treatment-voorhees-nj";
const TITLE =
  "Addiction Treatment Near Voorhees, NJ | Cherry Hill Recovery Center";
const DESCRIPTION =
  "Addiction treatment near Voorhees, NJ — PHP, IOP, MAT & detox referral at Cherry Hill Recovery Center. Most insurance accepted. Call 856-200-3127.";

const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "Helvetica, Arial, sans-serif";

type Pill = { label: string; href: string };

const programs: Pill[] = [
  { label: "Virtual IOP", href: "/virtual-iop-new-jersey" },
  { label: "Psychiatric Evaluation", href: "/psychiatric-evaluation-nj" },
  { label: "Drug & Alcohol Evaluation", href: "/drug-alcohol-evaluation-nj" },
];

const conditions: Pill[] = [
  { label: "Heroin & Opioid Addiction", href: "/heroin-opioid-addiction-treatment-nj" },
  { label: "Alcohol Addiction", href: "/alcohol-addiction-treatment-cherry-hill-nj" },
  { label: "Fentanyl Addiction", href: "/fentanyl-addiction-treatment-nj" },
  { label: "Prescription Painkillers", href: "/painkiller-addiction-treatment-nj" },
  { label: "Benzodiazepine Addiction", href: "/benzodiazepine-addiction-treatment-nj" },
  { label: "Cocaine Addiction", href: "/cocaine-addiction-treatment-nj" },
  { label: "Marijuana Addiction", href: "/marijuana-addiction-treatment-nj" },
  { label: "Adderall & Stimulants", href: "/adderall-addiction-treatment-nj" },
  { label: "Methadone", href: "/methadone-addiction-treatment-nj" },
  { label: "Synthetic Drugs (K2, Zaza)", href: "/synthetic-drug-treatment-nj" },
  { label: "Dual Diagnosis", href: "/psychiatric-evaluation-nj" },
];

const insurers: string[] = [
  "Aetna",
  "Anthem",
  "Blue Cross Blue Shield",
  "Cigna",
  "United Healthcare",
  "Humana",
  "Magellan",
  "Beacon",
  "Compsych",
  "Health Net",
  "Independence Administrators",
  "Carefirst",
  "& More",
];

const nearby: Pill[] = [
  { label: "Cherry Hill", href: "/" },
  { label: "Marlton", href: "/addiction-treatment-marlton-nj" },
  { label: "Mount Laurel", href: "/addiction-treatment-mount-laurel-nj" },
  { label: "Moorestown", href: "/addiction-treatment-moorestown-nj" },
  { label: "Haddonfield", href: "/addiction-treatment-haddonfield-nj" },
  { label: "Pennsauken", href: "/addiction-treatment-pennsauken-nj" },
  { label: "Camden", href: "/addiction-treatment-camden-nj" },
  { label: "Burlington County", href: "/addiction-treatment-burlington-county-nj" },
];

const faqs = [
  {
    q: "How far is Cherry Hill Recovery Center from Voorhees?",
    a: "Cherry Hill Recovery Center is located at 1930 Marlton Pike East in Cherry Hill — approximately 5 miles and 10 to 15 minutes from most of Voorhees Township. The most direct route is via Route 73 South to Marlton Pike East. For patients who prefer not to drive, our Virtual IOP delivers the same structured programming via telehealth from home.",
  },
  {
    q: "Do you accept insurance for Voorhees residents?",
    a: "Yes — we work with most major insurance plans, including Aetna, Cigna, United Healthcare, Blue Cross Blue Shield, Anthem, Optum, Carelon, and Harvard Pilgrim. Our admissions team verifies your specific benefits at no cost and walks you through exactly what your plan covers. Call 856-200-3127 or verify online in 60 seconds to get started.",
  },
  {
    q: "Can I attend treatment while still working in Voorhees?",
    a: "Yes. Our Intensive Outpatient Program runs three to four evenings per week starting between 5pm and 6pm — so patients from Voorhees can complete a full workday before attending. The commute from Voorhees is short and straightforward, and Virtual IOP is also available for patients who prefer to attend from home.",
  },
  {
    q: "What is the difference between PHP and IOP?",
    a: "PHP — Partial Hospitalization Program — provides five to six days per week of intensive treatment, typically five to eight hours per day. IOP — Intensive Outpatient Program — provides three to four days per week, approximately three hours per session, allowing patients to maintain work and family responsibilities. The right level is determined by a free clinical assessment our team conducts before treatment begins.",
  },
  {
    q: "Do you provide detox services?",
    a: "Cherry Hill Recovery Center does not operate an on-site detox facility. We assess each patient's needs and refer them to the most appropriate medically supervised detox provider — coordinating the entire process and transitioning patients directly into our PHP or IOP program upon completion.",
  },
  {
    q: "Are there addiction resources for Voorhees residents?",
    a: "Cherry Hill Recovery Center is the closest full-service outpatient addiction treatment provider to Voorhees Township — offering PHP, IOP, MAT, psychiatric evaluation, and detox referral. For additional NJ-wide resources, SAMHSA's helpline is available 24/7 at 1-800-662-4357, and NJ Mental Health Cares can be reached at 1-866-202-4357. Our admissions team is also available around the clock at 856-200-3127.",
  },
];

const PillLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    style={{
      display: "inline-flex",
      alignItems: "center",
      background: "#FFFFFF",
      border: "1px solid #F0E0E0",
      borderRadius: 50,
      padding: "10px 20px",
      color: "#8B1A1A",
      fontWeight: 700,
      fontSize: 13,
      margin: 5,
      textDecoration: "none",
      fontFamily: SANS,
    }}
  >
    {children}
  </Link>
);

const SidebarLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    style={{
      display: "block",
      color: "#8B1A1A",
      fontSize: 14,
      textDecoration: "none",
      padding: "6px 0",
    }}
  >
    {children}
  </Link>
);

const AddictionTreatmentVoorheesNJ = () => {
  
  const clinicSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: "Cherry Hill Recovery Center",
    description:
      "Addiction treatment serving Voorhees NJ — PHP, IOP, MAT and detox referral",
    url: CANONICAL,
    telephone: "856-200-3127",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1930 Marlton Pike East",
      addressLocality: "Cherry Hill",
      addressRegion: "NJ",
      postalCode: "08003",
    },
    areaServed: {
      "@type": "City",
      name: "Voorhees Township, New Jersey",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cherryhillrecoverycenter.com/" },
      { "@type": "ListItem", position: 2, name: "Addiction Treatment", item: "https://cherryhillrecoverycenter.com/" },
      { "@type": "ListItem", position: 3, name: "Voorhees, NJ", item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={[clinicSchema, faqSchema, breadcrumbSchema]} />
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" style={{ background: "#FAF4F0", padding: "12px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", fontSize: 13, color: "#2C2C2C", fontFamily: SANS }}>
          <Link to="/" style={{ color: "#8B1A1A", textDecoration: "none" }}>Home</Link>
          <span style={{ margin: "0 8px", color: "#999" }}>›</span>
          <Link to="/" style={{ color: "#8B1A1A", textDecoration: "none" }}>Addiction Treatment</Link>
          <span style={{ margin: "0 8px", color: "#999" }}>›</span>
          <span>Voorhees, NJ</span>
        </div>
      </nav>

      {/* HERO */}
      <header
        style={{
          background:
            "linear-gradient(90deg, #6B1414 0%, #8B1A1A 100%), radial-gradient(circle at 20% 30%, rgba(250,244,240,0.06) 1px, transparent 1px)",
          backgroundSize: "auto, 24px 24px",
          padding: "96px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center", width: "100%" }}>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              marginBottom: 12,
              fontFamily: SANS,
              fontWeight: 600,
            }}
          >
            Serving Voorhees, NJ and Surrounding Communities
          </p>
          <h1
            style={{
              fontFamily: SERIF,
              fontSize: 40,
              color: "#FFFFFF",
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            Addiction Treatment Near <span style={{ color: "#FAC8C8" }}>Voorhees, NJ</span>
          </h1>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 17,
              margin: "12px auto 24px",
              maxWidth: 600,
              lineHeight: 1.55,
              fontFamily: SANS,
            }}
          >
            PHP, IOP, MAT, and detox referral from Cherry Hill Recovery Center — minutes from Voorhees, most insurance accepted, available now.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                background: "#FAF4F0",
                color: "#8B1A1A",
                padding: "12px 26px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Call {PHONE}
            </a>
            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                background: "transparent",
                color: "#FAF4F0",
                border: "2px solid #FAF4F0",
                padding: "10px 24px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Verify Your Insurance
            </Link>
          </div>
        </div>
      </header>

      {/* TRUST BAR */}
      <div
        style={{
          background: "#FAF4F0",
          borderTop: "3px solid #8B1A1A",
          borderBottom: "1px solid #F0E0E0",
          padding: "16px 0",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 20px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: 13,
            color: "#2C2C2C",
            textAlign: "center",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <MapPin size={16} color="#8B1A1A" /> Minutes from Voorhees
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Check size={16} color="#8B1A1A" /> Most Insurance Accepted
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Shield size={16} color="#8B1A1A" /> Licensed & Accredited
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Phone size={16} color="#8B1A1A" /> Available 24/7
          </span>
        </div>
      </div>

      {/* SECTION 2 — LOCAL INTRO */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 20px",
            display: "grid",
            gridTemplateColumns: "minmax(0, 65fr) minmax(0, 35fr)",
            gap: 40,
          }}
          className="local-intro-grid"
        >
          <div>
            <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0, lineHeight: 1.2 }}>
              Addiction Treatment Serving Voorhees and South Jersey
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 18, fontFamily: SANS }}>
              Cherry Hill Recovery Center is located at 1930 Marlton Pike East in Cherry Hill — less than five miles from Voorhees Township and easily accessible via Route 73 and Haddonfield-Berlin Road. For Voorhees residents and families seeking addiction treatment, we offer{" "}
              <Link to="/php-cherry-hill-nj" style={{ color: "#8B1A1A" }}>PHP</Link>,{" "}
              <Link to="/iop-cherry-hill-nj" style={{ color: "#8B1A1A" }}>IOP</Link>,{" "}
              <Link to="/medication-assisted-treatment-nj" style={{ color: "#8B1A1A" }}>MAT</Link>, and{" "}
              <Link to="/detox-referral-coordination-nj" style={{ color: "#8B1A1A" }}>detox referral</Link>{" "}
              coordination without the need to travel far from home. Staying close to your community during treatment matters — and for patients in Voorhees, Cherry Hill Recovery Center provides that proximity alongside the clinical depth of a full-service addiction treatment program led by a board-certified medical and clinical team.
            </p>

            <h3 style={{ fontFamily: SANS, color: "#6B1414", fontSize: 22, marginTop: 32, fontWeight: 700 }}>
              Addiction and Recovery in Camden County
            </h3>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 12, fontFamily: SANS }}>
              Camden County — where both Voorhees Township and Cherry Hill are located — has been significantly affected by New Jersey's addiction crisis. Opioids, alcohol, and fentanyl-contaminated substances affect people across every income level, age group, and community in Camden County. The need for accessible, high-quality outpatient addiction treatment in our community is real and it is exactly what Cherry Hill Recovery Center was built to address.
            </p>

            <h3 style={{ fontFamily: SANS, color: "#6B1414", fontSize: 22, marginTop: 32, fontWeight: 700 }}>
              Why Voorhees Residents Choose Cherry Hill Recovery Center
            </h3>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 12, fontFamily: SANS }}>
              For patients from Voorhees, the combination of proximity and clinical quality is the primary reason they choose Cherry Hill Recovery Center. Our location on Marlton Pike East is a straightforward drive from virtually any part of Voorhees Township — and for patients in our IOP program attending three to four evenings per week, that convenience matters meaningfully over the course of a program. We also serve Voorhees families through our{" "}
              <Link to="/virtual-iop-new-jersey" style={{ color: "#8B1A1A" }}>Virtual IOP</Link>{" "}
              option — delivering the same structured programming via telehealth for patients who prefer to attend from home.
            </p>
          </div>

          {/* SIDEBAR */}
          <aside
            style={{
              position: "sticky",
              top: 90,
              alignSelf: "start",
              background: "#FAF4F0",
              border: "1px solid #F0E0E0",
              borderTop: "4px solid #8B1A1A",
              borderRadius: 12,
              padding: 28,
              fontFamily: SANS,
            }}
          >
            <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: 0 }}>Get Help Today</h3>
            <p style={{ color: "#2C2C2C", fontSize: 14, marginTop: 10, lineHeight: 1.55 }}>
              Free confidential assessment. Most insurance accepted. Same-day response.
            </p>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                display: "block",
                background: "#8B1A1A",
                color: "#FAF4F0",
                padding: "12px 16px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 14,
                marginTop: 14,
                textAlign: "center",
              }}
            >
              Call {PHONE}
            </a>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                display: "block",
                background: "transparent",
                color: "#8B1A1A",
                border: "2px solid #8B1A1A",
                padding: "10px 16px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 14,
                textAlign: "center",
              }}
            >
              Verify My Insurance
            </Link>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <p style={{ color: "#2C2C2C", fontWeight: 700, fontSize: 13, margin: 0 }}>Programs Available:</p>
            <div style={{ marginTop: 8 }}>
              <SidebarLink to="/php-cherry-hill-nj">→ Partial Hospitalization (PHP)</SidebarLink>
              <SidebarLink to="/iop-cherry-hill-nj">→ Intensive Outpatient (IOP)</SidebarLink>
              <SidebarLink to="/virtual-iop-new-jersey">→ Virtual IOP & Telehealth</SidebarLink>
              <SidebarLink to="/medication-assisted-treatment-nj">→ Medication Assisted Treatment</SidebarLink>
              <SidebarLink to="/detox-referral-coordination-nj">→ Detox Referral & Coordination</SidebarLink>
              <SidebarLink to="/psychiatric-evaluation-nj">→ Psychiatric Evaluations</SidebarLink>
            </div>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <p style={{ color: "#2C2C2C", fontWeight: 700, fontSize: 13, margin: 0 }}>Our Location:</p>
            <p style={{ color: "#888", fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              1930 Marlton Pike East
              <br />
              Cherry Hill, NJ 08003
            </p>
            <a
              href="https://maps.google.com/maps?daddr=1930+Marlton+Pike+East+Cherry+Hill+NJ+08003"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#8B1A1A", fontSize: 13, fontWeight: 700, textDecoration: "none" }}
            >
              Get Directions →
            </a>
          </aside>
        </div>
      </section>

      {/* SECTION 3 — DISTANCE & ACCESS */}
      <section style={{ background: "#FAF4F0", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px" }}>
          <h2
            style={{
              fontFamily: SERIF,
              color: "#8B1A1A",
              fontSize: 28,
              textAlign: "center",
              margin: 0,
              marginBottom: 32,
            }}
          >
            Getting to Cherry Hill Recovery Center from Voorhees
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            {[
              {
                Icon: Car,
                title: "By Car",
                body:
                  "Approximately 10 to 15 minutes from most of Voorhees Township. Take Route 73 South to Marlton Pike East (Route 70), or use Haddonfield-Berlin Road to connect directly to our Cherry Hill location.",
                small: "1930 Marlton Pike East, Cherry Hill, NJ 08003",
              },
              {
                Icon: Clock,
                title: "Evening IOP Timing",
                body:
                  "Our evening IOP sessions typically begin at 5pm to 6pm — Voorhees patients can leave work, make a 10 to 15 minute drive, and arrive at treatment without a difficult commute.",
              },
              {
                Icon: Monitor,
                title: "Virtual Option",
                body:
                  "Prefer not to drive? Our Virtual IOP delivers the same structured programming via secure telehealth — available to Voorhees residents from home. Same clinical quality, zero commute.",
              },
            ].map(({ Icon, title, body, small }) => (
              <div
                key={title}
                style={{
                  background: "#FFFFFF",
                  borderRadius: 12,
                  padding: 24,
                  borderTop: "3px solid #8B1A1A",
                  textAlign: "center",
                  boxShadow: "0 6px 18px rgba(139,26,26,0.06)",
                  fontFamily: SANS,
                }}
              >
                <Icon size={24} color="#8B1A1A" />
                <h3 style={{ color: "#2C2C2C", fontSize: 18, fontWeight: 700, marginTop: 10 }}>{title}</h3>
                <p style={{ color: "#555", fontSize: 14, lineHeight: 1.6, marginTop: 8 }}>{body}</p>
                {small && (
                  <p style={{ color: "#888", fontSize: 12, marginTop: 10 }}>{small}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — PROGRAMS */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 32, textAlign: "center", margin: 0 }}>
            Addiction Treatment Programs Available to Voorhees Residents
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 17,
              maxWidth: 700,
              margin: "16px auto 36px",
              textAlign: "center",
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Every program is available to patients from Voorhees and surrounding communities. Most insurance plans accepted — our admissions team verifies your benefits before anything starts.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 22,
            }}
          >
            {[
              {
                Icon: Calendar,
                title: "Partial Hospitalization Program (PHP)",
                body:
                  "Five to six days per week of intensive outpatient treatment. Individual therapy, group counseling, psychiatric evaluation, and medication management. Patients from Voorhees attend daily and return home each evening.",
                link: "/php-cherry-hill-nj",
                linkText: "Learn About PHP →",
              },
              {
                Icon: Clock,
                title: "Intensive Outpatient Program (IOP)",
                body:
                  "Three to four days per week of structured treatment. Evening and virtual scheduling available for Voorhees patients who need to maintain work, school, or family responsibilities.",
                link: "/iop-cherry-hill-nj",
                linkText: "Learn About IOP →",
              },
              {
                Icon: Pill,
                title: "Medication Assisted Treatment (MAT)",
                body:
                  "Suboxone and Sublocade for opioid use disorder — integrated into PHP or IOP when clinically appropriate and agreed upon by the clinical team and patient. Never provided as a standalone prescription.",
                link: "/medication-assisted-treatment-nj",
                linkText: "Learn About MAT →",
              },
              {
                Icon: Network,
                title: "Detox Referral & Coordination",
                body:
                  "We assess each patient's needs and refer them to the right medically supervised detox provider — coordinating the process and transitioning patients directly into PHP or IOP upon completion.",
                link: "/detox-referral-coordination-nj",
                linkText: "Learn About Detox Referral →",
              },
            ].map(({ Icon, title, body, link, linkText }) => (
              <div
                key={title}
                style={{
                  background: "#FAF4F0",
                  borderRadius: 12,
                  padding: 28,
                  borderTop: "4px solid #8B1A1A",
                  boxShadow: "0 6px 18px rgba(107,20,20,0.08)",
                  fontFamily: SANS,
                }}
              >
                <Icon size={26} color="#8B1A1A" />
                <h3 style={{ color: "#2C2C2C", fontSize: 19, fontWeight: 700, marginTop: 10 }}>{title}</h3>
                <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.65, marginTop: 10 }}>{body}</p>
                <Link
                  to={link}
                  style={{
                    display: "inline-block",
                    color: "#8B1A1A",
                    fontWeight: 700,
                    fontSize: 14,
                    textDecoration: "none",
                    marginTop: 12,
                  }}
                >
                  {linkText}
                </Link>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginTop: 28 }}>
            {programs.map((p) => (
              <PillLink key={p.href} to={p.href}>
                {p.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4B — ALCOHOL FOR VOORHEES */}
      <section style={{ background: "#FAF4F0", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2
            style={{
              fontFamily: SERIF,
              color: "#8B1A1A",
              fontSize: 32,
              textAlign: "center",
              margin: 0,
            }}
          >
            Alcohol Rehab for Voorhees Residents
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16.5,
              lineHeight: 1.75,
              fontFamily: SANS,
              maxWidth: 820,
              margin: "22px auto 0",
            }}
          >
            Alcohol treatment at Cherry Hill Recovery Center is outpatient —
            delivered through PHP and IOP with daytime and evening groups, a 10
            to 15 minute drive from Voorhees on Route 70. Before anything
            starts, every patient has a psychiatric evaluation and a drug &amp;
            alcohol evaluation, so the plan is built around what is actually
            happening rather than a template. When medication support helps, it
            is prescribed and managed in-house by our psychiatric team.
            Anxiety, depression, and trauma are treated alongside drinking,
            because the two so often travel together and one rarely settles
            while the other is ignored. If medical detox is needed first, we do
            not provide it here — we coordinate with a detox partner and resume
            your care the day you finish, so there is no gap between the two.
            Most patients from Voorhees keep working or managing family
            responsibilities throughout treatment, since both levels of care are
            built around a normal schedule.{" "}
            <Link
              to="/alcohol-addiction-treatment-cherry-hill-nj"
              style={{
                color: "#8B1A1A",
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              Learn more about alcohol rehab in Cherry Hill, NJ
            </Link>
          </p>
        </div>
      </section>

      {/* SECTION 4C — STIMULANTS FOR VOORHEES */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2
            style={{
              fontFamily: SERIF,
              color: "#8B1A1A",
              fontSize: 32,
              textAlign: "center",
              margin: 0,
            }}
          >
            Cocaine, Adderall and Stimulant Addiction Treatment Near Voorhees
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16.5,
              lineHeight: 1.75,
              fontFamily: SANS,
              maxWidth: 820,
              margin: "22px auto 0",
            }}
          >
            Stimulant use disorders — cocaine, crack, Adderall, and
            methamphetamine — are treated in the same PHP and IOP structure as
            everything else we do, so a patient from Voorhees does not have to
            switch clinics when the drug changes. There is no FDA-approved
            medication for stimulant addiction, which is why therapy intensity
            matters: the crash and the depression that follow stopping are real
            clinical problems, and we address them directly with psychiatric
            care alongside CBT and contingency-based approaches that reinforce
            showing up and staying engaged. Dual diagnosis care is included,
            not added on, because many stimulant patients arrive with ADHD,
            trauma, or a mood disorder that has been driving the use. Consistent
            structure is the treatment here, so we build a full week of
            programming and adjust the level of care as recovery holds. Read
            more about{" "}
            <Link
              to="/cocaine-addiction-treatment-nj"
              style={{
                color: "#8B1A1A",
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
              }}
            >
              cocaine addiction treatment
            </Link>{" "}
            and{" "}
            <Link
              to="/adderall-addiction-treatment-nj"
              style={{
                color: "#8B1A1A",
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
              }}
            >
              Adderall addiction treatment
            </Link>
            .
          </p>
        </div>
      </section>

      {/* SECTION 5 — CONDITIONS */}
      <section style={{ background: "#FAF4F0", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, textAlign: "center", margin: 0 }}>
            Conditions We Treat
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16,
              textAlign: "center",
              maxWidth: 760,
              margin: "14px auto 28px",
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Our clinical team treats the full range of substance use disorders alongside co-occurring mental health conditions — serving patients from Voorhees, Cherry Hill, and all of South Jersey.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
            {conditions.map((c) => (
              <PillLink key={c.href + c.label} to={c.href}>
                {c.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — LOCAL STATS */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0 }}>
            Addiction in Camden County — Why Local Treatment Matters
          </h2>
          <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 16, fontFamily: SANS }}>
            New Jersey consistently ranks among the states most severely affected by the opioid and addiction crisis — and Camden County reflects that reality. Fentanyl is now present in the majority of opioid supplies across South Jersey. Alcohol use disorder remains the most common addiction in the county. And the majority of people who need treatment are not in residential care — they are working, raising families, and living in communities like Voorhees. They need treatment that meets them where they are.
          </p>
          <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 14, fontFamily: SANS }}>
            Outpatient addiction treatment — PHP and IOP — is not a lesser option. For the majority of people with substance use disorders, structured outpatient treatment is the clinically appropriate level of care. It is evidence-based, covered by most insurance plans, and it is what Cherry Hill Recovery Center is built to deliver to Voorhees residents and communities across South Jersey.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 20,
              marginTop: 32,
            }}
          >
            {[
              { num: "#1", label: "Fentanyl — leading cause of overdose death in New Jersey" },
              { num: "Most", label: "People with addiction need outpatient — not residential — treatment" },
              { num: "24/7", label: "Cherry Hill Recovery Center admissions team availability" },
            ].map((s) => (
              <div
                key={s.num + s.label}
                style={{
                  background: "#FAF4F0",
                  borderRadius: 12,
                  padding: 24,
                  borderTop: "3px solid #8B1A1A",
                  textAlign: "center",
                  fontFamily: SANS,
                }}
              >
                <div style={{ color: "#8B1A1A", fontWeight: 700, fontSize: 32 }}>{s.num}</div>
                <div style={{ color: "#555", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — INSURANCE */}
      <section style={{ background: "#FAF4F0", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0 }}>
            Insurance Coverage for Voorhees Residents
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16,
              maxWidth: 680,
              margin: "16px auto 28px",
              lineHeight: 1.7,
              fontFamily: SANS,
            }}
          >
            We work with most major insurance plans held by New Jersey residents — including Aetna, Cigna, United Healthcare, Blue Cross Blue Shield, Anthem, Optum, Carelon, and Harvard Pilgrim. Under the Mental Health Parity Act, most plans are required to cover addiction treatment at the same level as other medical conditions. Our admissions team verifies your specific benefits before treatment begins at no cost — so you know exactly what your plan covers before committing to anything.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginBottom: 32 }}>
            <InsurancePills insurers={insurers} style={{ justifyContent: "center" }} />
          </div>
          <Link
            to="/verify-insurance"
            aria-label="Verify your insurance — free"
            style={{
              display: "inline-block",
              background: "#8B1A1A",
              color: "#FAF4F0",
              padding: "14px 36px",
              borderRadius: 999,
              fontWeight: 700,
              textDecoration: "none",
              fontSize: 15,
              fontFamily: SANS,
            }}
          >
            Verify My Insurance — Free
          </Link>
        </div>
      </section>

      {/* SECTION 8 — NEARBY AREAS */}
      <section style={{ background: "#FFFFFF", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 26, margin: 0 }}>
            Other Communities We Serve Near Voorhees
          </h2>
          <p style={{ color: "#555", fontSize: 15, marginTop: 14, marginBottom: 20, fontFamily: SANS }}>
            Cherry Hill Recovery Center serves patients from throughout South Jersey and the greater Philadelphia region.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
            {nearby.map((n) => (
              <PillLink key={n.href + n.label} to={n.href}>
                {n.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — MAP */}
      <section style={{ background: "#FAF4F0", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 24, margin: 0, marginBottom: 20 }}>
            Our Location — Minutes from Voorhees, NJ
          </h2>
          <LazyMap src="https://www.google.com/maps?q=1930+Marlton+Pike+East,+Cherry+Hill,+NJ+08003&z=13&output=embed" title="Cherry Hill Recovery Center — serving Voorhees NJ" height={340} />
          <div style={{ marginTop: 16, fontFamily: SANS }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#2C2C2C" }}>Cherry Hill Recovery Center</div>
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              1930 Marlton Pike East, Cherry Hill, NJ 08003
            </div>
            <a
              href="https://maps.google.com/maps?saddr=Voorhees+Township+NJ&daddr=1930+Marlton+Pike+East+Cherry+Hill+NJ+08003"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#8B1A1A", fontSize: 13, fontWeight: 700, textDecoration: "none", display: "inline-block", marginTop: 10 }}
            >
              Get Directions from Voorhees →
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 10 — FAQ */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0, marginBottom: 24 }}>
            Frequently Asked Questions — Addiction Treatment Near Voorhees, NJ
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                style={{
                  background: "#FFFFFF",
                  border: 0,
                  borderBottom: "1px solid #F0E0E0",
                  borderRadius: 0,
                  paddingLeft: 8,
                  paddingRight: 8,
                }}
              >
                <AccordionTrigger className="hover:no-underline py-5 text-left">
                  <h3
                    style={{
                      color: "#2C2C2C",
                      fontSize: 16,
                      fontWeight: 600,
                      margin: 0,
                      fontFamily: SANS,
                    }}
                  >
                    {f.q}
                  </h3>
                </AccordionTrigger>
                <AccordionContent
                  style={{
                    background: "#FAF4F0",
                    color: "#2C2C2C",
                    fontSize: 15,
                    lineHeight: 1.7,
                    padding: "16px 20px",
                    borderRadius: 8,
                    fontFamily: SANS,
                  }}
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 11 — BOTTOM CTA */}
      <section
        style={{
          background: "linear-gradient(135deg, #6B1414 0%, #8B1A1A 100%)",
          padding: "64px 20px",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 36, color: "#FFFFFF", margin: 0, lineHeight: 1.2 }}>
            Addiction Treatment Minutes From Voorhees — Available Now.
          </h2>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 18,
              maxWidth: 600,
              margin: "16px auto 0",
              lineHeight: 1.55,
              fontFamily: SANS,
            }}
          >
            PHP, IOP, MAT, and detox referral from Cherry Hill Recovery Center. Free assessment, most insurance accepted, same-day response.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 24 }}>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                background: "#FAF4F0",
                color: "#8B1A1A",
                padding: "12px 26px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Call {PHONE}
            </a>
            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                background: "transparent",
                color: "#FAF4F0",
                border: "2px solid #FAF4F0",
                padding: "10px 24px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Verify Your Insurance
            </Link>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 18,
              marginTop: 24,
              color: "rgba(250,200,200,0.9)",
              fontSize: 13,
              fontFamily: SANS,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Lock size={14} /> 100% Confidential
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Check size={14} /> Insurance Accepted
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Phone size={14} /> Same-Day Response
            </span>
          </div>
        </div>
      </section>

      <RelatedLinks exclude={["/addiction-treatment-voorhees-nj"]} towns={["Cherry Hill", "Marlton", "Haddonfield", "Camden", "Mount Laurel"]} />

      <Footer />
      <FloatingCTAs />

      <style>{`
        @media (max-width: 900px) {
          .local-intro-grid { grid-template-columns: 1fr !important; }
          .local-intro-grid aside { position: static !important; }
        }
      `}</style>
    </div>
  );
};

export default AddictionTreatmentVoorheesNJ;
